import { LeaderboardEntry } from './constants';
import { apiFetch } from '../config';

const STORAGE_KEY = 'neuro_duel_leaderboard';
const MAX_ENTRIES = 50;
const REFRESH_INTERVAL = 5000;

export function saveScore(entry: Omit<LeaderboardEntry, 'timestamp'>): void {
  const entries = getLeaderboard();
  const full: LeaderboardEntry = {
    ...entry,
    timestamp: Date.now(),
  };

  const playerName = entry.playerName || 'Player';
  const existingIndex = entries.findIndex(e => (e.playerName || 'Player') === playerName);
  
  if (existingIndex >= 0) {
    if (entry.score > entries[existingIndex].score) {
      entries[existingIndex] = full;
    }
  } else {
    entries.push(full);
  }

  entries.sort((a, b) => b.score - a.score);
  const top = entries.slice(0, MAX_ENTRIES);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(top));
  } catch {
    // Storage full or unavailable
  }
}

export function getLeaderboard(): LeaderboardEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function subscribeToLeaderboard(callback: (entries: LeaderboardEntry[]) => void): () => void {
  let current = getLeaderboard();
  callback(current);
  
  const interval = setInterval(() => {
    const fresh = getLeaderboard();
    const changed = fresh.length !== current.length || 
      fresh.some((e, i) => e.score !== current[i]?.score || e.playerName !== current[i]?.playerName);
    if (changed) {
      current = fresh;
      callback(fresh);
    }
  }, REFRESH_INTERVAL);
  
  return () => clearInterval(interval);
}

export function getBestScore(): number {
  const entries = getLeaderboard();
  return entries.length > 0 ? entries[0].score : 0;
}

export function getBestTime(): number {
  const entries = getLeaderboard();
  if (entries.length === 0) return Infinity;
  return Math.min(...entries.map((e) => e.battleTime));
}

export function getLongestCombo(): number {
  const entries = getLeaderboard();
  return entries.length > 0 ? Math.max(...entries.map((e) => e.maxCombo)) : 0;
}

export function getHighestHitRate(): number {
  const entries = getLeaderboard();
  return entries.length > 0 ? Math.max(...entries.map((e) => e.hitRate)) : 0;
}

export function clearLeaderboard(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export async function clearBackendLeaderboard(): Promise<void> {
  try {
    await apiFetch('/api/leaderboard', { method: 'DELETE' });
    clearLeaderboard();
  } catch {
    // ignore
  }
}
